import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { MFA_QR_CODE_SIZE, useSetupMfaPage } from './useSetupMfaPage';

import { notify } from '@speeki/global-ui-components';
import {
    AuthenticationCode,
    Button,
    PinField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';
import { ColumnsLayout } from '@components/templates/ColumnsLayout/ColumnsLayout';

import { useResponsive } from '@utils/hooks/useResponsive';

import { getPath } from '@services/RouteService/paths';

import { CardStep } from '@domains/auth/atoms/CardStep/CardStep';
import { MfaCard, MfaCardStepWrapper } from '@domains/auth/ions/cards';
import { FormBottomElement } from '@domains/auth/molecules/FormBottomElement/FormBottomElement';
import { PointsFrame } from '@domains/auth/molecules/PointsFrame/PointsFrame';

const listPoints = [
    'MFA_POINTS_FRAME_FIRST_POINT',
    'MFA_POINTS_FRAME_SECOND_POINT',
    'MFA_POINTS_FRAME_THIRD_POINT',
];

export const SetupMfaPage = () => {
    const isXs = useResponsive('xs');
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        clearErrors,
        confirmMfaLoading,
        errors,
        field,
        handleSkipMfa,
        isSubmitted,
        isSubmitting,
        metadata,
        onChange,
        onSubmit,
        qrCode,
        secret,
        setShowManualCode,
        showManualCode,
    } = useSetupMfaPage();

    if (metadata?.isAppAssigned)
        return (
            <Navigate
                to={getPath({ path: '/auth/setup-mfa/recovery-email' })}
            />
        );

    return (
        <ColumnsLayout>
            <MfaCard testId="LBukAei">
                <MfaCard.TitleSecondary>
                    {t('SETUP_MFA_HEADER_FIRST_LINE')}
                    <br /> {t('SETUP_MFA_HEADER_SECOND_LINE')}
                </MfaCard.TitleSecondary>
                <MfaCardStepWrapper>
                    <CardStep
                        text={t('SETUP_MFA_FIRST_STEP_TEXT')}
                        title={t('SETUP_MFA_FIRST_STEP_TITLE')}
                    />
                </MfaCardStepWrapper>
                <MfaCardStepWrapper>
                    <CardStep
                        text={
                            isXs
                                ? t('SETUP_MFA_SECOND_STEP_TEXT_ON_MOBILE')
                                : showManualCode
                                  ? t('SETUP_MFA_SECOND_STEP_TEXT_MANUAL')
                                  : t('SETUP_MFA_SECOND_STEP_TEXT_QR')
                        }
                        title={
                            isXs
                                ? t('SETUP_MFA_SECOND_STEP_TITLE_ON_MOBILE')
                                : showManualCode
                                  ? t('SETUP_MFA_SECOND_STEP_TITLE_MANUAL')
                                  : t('SETUP_MFA_SECOND_STEP_TITLE_QR')
                        }
                    >
                        {secret && qrCode && (
                            <AuthenticationCode
                                code={secret}
                                copyCallback={
                                    notify(t('COPY_TO_CLIPBOARD')).INFO
                                }
                                isXs={isXs}
                                manualText={t('SCAN_QR_CODE_INSTEAD')}
                                qrCode={
                                    <QRCodeCanvas
                                        id="mfa-qrcode"
                                        size={MFA_QR_CODE_SIZE}
                                        value={qrCode}
                                    />
                                }
                                qrCodeText={t('CANT_SCAN_QR_CODE')}
                                setShowManualCode={setShowManualCode}
                                showManualCode={showManualCode}
                                testId="ibQ4Fzz"
                                withCopying
                            />
                        )}
                    </CardStep>
                </MfaCardStepWrapper>
                <MfaCardStepWrapper>
                    <CardStep
                        text={t('SETUP_MFA_THIRD_STEP_TEXT')}
                        title={t('SETUP_MFA_THIRD_STEP_TITLE')}
                    >
                        {/*@ts-expect-error TODO: something wrong with the types*/}
                        <PinField
                            className="htmlToImageIgnore"
                            error={!!errors.code}
                            message={t(errors.code?.message || '')}
                            name={field.name}
                            onChange={(value) => {
                                clearErrors('code');
                                onChange(value);
                            }}
                            ref={field.ref}
                            spacing={8}
                            type="number"
                        />
                    </CardStep>
                </MfaCardStepWrapper>
                <StandardForm.Actions>
                    <Button
                        disabled={isSubmitting || (errors.code && isSubmitted)}
                        fullWidth
                        isLoading={isSubmitting || confirmMfaLoading}
                        onClick={onSubmit}
                        testId="button_next"
                        variant="primary"
                    >
                        {t('NEXT')}
                    </Button>
                    <FormBottomElement
                        buttonText={t('SET_UP_LATER')}
                        disabled={isSubmitting || confirmMfaLoading}
                        onSubmit={() =>
                            handleSkipMfa(
                                {},
                                {
                                    onSuccess: () =>
                                        navigate(getPath({ path: '/' })),
                                },
                            )
                        }
                    />
                </StandardForm.Actions>
            </MfaCard>
            <PointsFrame
                header={t('MFA_POINTS_FRAME_HEADER')}
                items={listPoints.map((point) => t(point))}
            />
        </ColumnsLayout>
    );
};
